import { useEffect, useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, Eye, EyeOff } from 'lucide-react';
import SEO from '@/components/SEO';
import logoCygnature from '@/assets/logo-cygnature.png';
import { toast } from 'sonner';
import { translateAuthError } from './translateAuthError';

export default function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Supabase auth-helpers handle the recovery token from URL automatically.
    // We just verify a session is established.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') setReady(true);
    });
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) setReady(true);
    });
    return () => subscription.unsubscribe();
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (password.length < 8) return toast.error('8 caractères minimum.');
    if (password !== confirm) return toast.error('Les mots de passe ne correspondent pas.');
    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (error) toast.error(translateAuthError(error.message));
    else {
      toast.success('Mot de passe défini. Bienvenue !');
      navigate('/crm-pro-evenementiel', { replace: true });
    }
  };

  return (
    <>
      <SEO title="Définir un mot de passe" description="Activation de compte." noindex />
      <div className="min-h-screen flex items-center justify-center px-4 bg-slate-950">
        <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-lg space-y-6">
          <div className="flex flex-col items-center text-center space-y-3">
            <img src={logoCygnature} alt="Cygnature" className="h-14 w-auto" />
            <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center">
              <Lock className="text-amber-400" strokeWidth={1.5} size={18} />
            </div>
            <h1 className="text-xl font-serif text-slate-100">Définir votre mot de passe</h1>
            <p className="text-xs text-slate-400">Choisissez un mot de passe pour activer votre accès au CRM.</p>
          </div>

          {!ready ? (
            <p className="text-center text-sm text-slate-400">Vérification du lien…</p>
          ) : (
            <form onSubmit={submit} className="space-y-4" autoComplete="on" method="post">
              <input type="text" name="username" autoComplete="username" defaultValue="" hidden readOnly />
              <div className="space-y-2">
                <Label htmlFor="pwd" className="text-slate-300">Nouveau mot de passe</Label>
                <div className="relative">
                  <Input id="pwd" name="new-password" type={showPwd ? 'text' : 'password'} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} className="pr-10 bg-slate-950 border-slate-800 text-slate-100" />
                  <button type="button" onClick={() => setShowPwd((v) => !v)} className="absolute inset-y-0 right-0 px-3 flex items-center text-slate-400 hover:text-slate-200" aria-label={showPwd ? 'Masquer' : 'Afficher'}>
                    {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="pwd2" className="text-slate-300">Confirmation</Label>
                <div className="relative">
                  <Input id="pwd2" name="confirm-password" type={showConfirm ? 'text' : 'password'} autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={8} className="pr-10 bg-slate-950 border-slate-800 text-slate-100" />
                  <button type="button" onClick={() => setShowConfirm((v) => !v)} className="absolute inset-y-0 right-0 px-3 flex items-center text-slate-400 hover:text-slate-200" aria-label={showConfirm ? 'Masquer' : 'Afficher'}>
                    {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950">
                {loading ? 'Enregistrement…' : 'Activer mon compte'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </>
  );
}
